import { MarketingShell } from "@/components/landing/MarketingShell";
import { ScrollToTop } from "@/components/landing/ScrollToTop";
import { fetchGhost, GhostPost } from "@/lib/ghost";
import { GetStaticProps } from "next";
import { Calendar, Clock, ExternalLink } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/landing/Navbar";
import Link from "next/link";
import { formatDate } from "@/lib/utils";

interface BlogPageProps {
  featured: GhostPost | null;
  latest: GhostPost[];
  releases: GhostPost[];
}

const BlogPage = ({ featured, latest, releases }: BlogPageProps) => {
  return (
    <MarketingShell className="flex flex-col justify-center">
      <div className="container space-y-8 md:space-y-16 py-12 md:py-20">
        <div className="text-center space-y-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">Blog</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Latest articles, tutorials, and product updates
          </p>
        </div>

        {featured && (
          <section>
            <Link
              href={featured.url}
              target="_blank"
              rel="noopener noreferrer"
              className="relative isolate flex flex-col justify-end overflow-hidden rounded-2xl bg-muted px-8 pb-8 pt-80 md:pt-96 group transition-transform hover:scale-[1.01]"
            >
              <img
                src={featured.feature_image ?? "/cover.jpg"}
                alt={featured.title}
                className="absolute inset-0 -z-10 size-full object-cover"
              />
              <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-background/60" />
              <div className="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-border" />

              <div className="flex flex-wrap items-center gap-y-1 overflow-hidden text-sm text-muted-foreground">
                <time className="mr-6 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(featured.published_at)}
                </time>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {featured.reading_time} min read
                </div>
              </div>
              <h3 className="mt-3 text-2xl md:text-3xl font-bold text-foreground transition-colors">
                {featured.title}
              </h3>
              {featured.excerpt && (
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                  {featured.excerpt}
                </p>
              )}
            </Link>
          </section>
        )}

        {latest.length > 0 && (
          <section>
            <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">Latest Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {latest.map(post => (
                <Link
                  key={post.id}
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative isolate flex flex-col justify-end overflow-hidden rounded-2xl bg-muted px-6 pb-6 pt-64 sm:pt-48 lg:pt-64 group transition-transform hover:scale-[1.02]"
                >
                  <img
                    src={post.feature_image ?? "/cover.jpg"}
                    alt={post.title}
                    className="absolute inset-0 -z-10 size-full object-cover"
                  />
                  <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background/90 via-background/50" />
                  <div className="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-border" />

                  <div className="flex flex-wrap items-center gap-y-1 overflow-hidden text-xs text-muted-foreground">
                    <time className="mr-4 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.published_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                      })}
                    </time>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.reading_time} min
                    </div>
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-foreground transition-colors">
                    {post.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}

        {releases.length > 0 && (
          <section>
            <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">Latest Releases</h2>
            <div className="space-y-4 md:space-y-6">
              {releases.map(release => (
                <Card
                  key={release.id}
                  className="bg-muted/50 border transition-shadow hover:shadow-md"
                >
                  <CardHeader>
                    <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon />
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            {formatDate(release.published_at)}
                          </span>
                        </div>
                        <CardTitle className="text-2xl">{release.title}</CardTitle>

                        {release.excerpt && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                            {release.excerpt}
                          </p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          href={release.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          View Release
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>
        )}

        <div>
          <Button asChild>
            <Link href="https://docs.dollardeploy.com/blog">View all articles</Link>
          </Button>
        </div>
      </div>

      <ScrollToTop />
    </MarketingShell>
  );
};

export const getStaticProps: GetStaticProps<BlogPageProps> = async () => {
  const blog = (await fetchGhost(["#blog"], 20))
    ?.filter(p => !p.tags?.find(t => t.name === "Changelog"))
    .slice(0, 4);
  const releases = await fetchGhost(["#blog", "Changelog"], 3);
  const featured = blog.length > 0 ? blog[0] : null;
  const latest = blog.length > 1 ? blog.slice(1) : [];

  return {
    props: {
      featured,
      latest,
      releases
    },
    revalidate: 3600
  };
};

export default BlogPage;
